// src/pages/Practice.jsx
import React, {
   useMemo,
   useRef,
   useState,
   useEffect,
   useCallback,
} from "react";
import { ReactSVG } from "react-svg";
import AlertPills from "../Utils/AlertPills";
import playIcon from "../../assets/svg/mdi--play.svg";

import {
   startPracticeSession,
   startPracticeSessionByCategory,
   getPracticeSession,
   submitPracticeAnswer,
   getPracticeQuestionStats,
   getPracticeTicketsOverview,
   resetPracticeSession,
} from "../../api/examService";

import {
   getQuestionCategories,
   getQuestionCategoriesWithCount,
} from "../../api/questionCategoriesService";
import {
   buildPracticeCorrectMap,
   buildResumeState,
   normalizeSelectedToZeroBased,
} from "../../utils/practiceResume";
import QuestionStatBar from "./QuestionStatBar";
import { ButtonUI, ChoiceUI } from "../UI";

const STUDENT_SCROLL_MOBILE_OFFSET = 75;
const getStudentQuestionScrollOffset = () =>
   window.matchMedia?.("(max-width: 860px)")?.matches
      ? STUDENT_SCROLL_MOBILE_OFFSET
      : 0;

/* ================= i18n ================= */
const I18N = {
   ro: {
      practice_title: "Testare",
      lang_ro: "Română",
      lang_ru: "Русский",
      ticket_n: "Bilet {n}",
      start_ticket_title: "Start bilet {n}",
      back: "Înapoi",
      question_index: "Întrebarea {i}/{total}",
      question_n: "Întrebarea {n}",
      question_alt: "Întrebare",
      answered_count: "Răspunse: {n}",
      left_count: "Nerăspunse: {n}",
      correct_count: "Corecte: {n}",
      wrong_count: "Greșite: {n}",
      next: "Următorul",
      finish: "Finalizează",
      choose_answer: "Alege răspunsul",
      hint_show: "Indiciu",
      hint_hide: "Ascunde indiciul",
      hint_empty: "Nu există indiciu pentru această întrebare.",
      cannot_start_practice: "Nu am putut porni/relua sesiunea de practică.",
      cannot_submit: "Nu am putut trimite răspunsul.",
      session_closed_switch_local:
         "Sesiunea server s-a închis/expirat. Trec pe mod local curat.",

      // ✅ home tabs
      tickets_tab: "Bilete",
      categories_tab: "Teme",
      refresh: "Refresh",
      categories_loading: "Se încarcă categoriile…",
      categories_empty: "Nu există categorii.",
      categories_load_failed: "Nu am putut încărca categoriile.",
      in_progress_short: "În curs",
      questions_count: "Întrebări",
      local_progress_note:
         "{name}: se restaurează răspunsurile salvate local din încercarea anterioară.",
      resume_ticket_title: "Continuă biletul {n}",
      reset_ticket: "Resetează biletul",
      reset_confirm: "Ștergi răspunsurile din acest bilet? Statistica rămâne.",
      reset_yes: "Da, resetează",
      reset_no: "Nu",
      reset_failed: "Nu am putut reseta biletul.",
      pass_aria: "Corecte {ok}, greșite {bad} din {total}",
      hard_tab: "Întrebări dificile",
      soon: "În curând",
      progress_aria: "Verzi {ok}, roșii {bad} din {total} întrebări",
      question_stat_aria: "La această întrebare: corect de {ok} ori, greșit de {bad} ori",
   },
   ru: {
      practice_title: "Тестирование",
      lang_ro: "Română",
      lang_ru: "Русский",
      ticket_n: "Билет {n}",
      start_ticket_title: "Открыть билет {n}",
      back: "Назад",
      question_index: "Вопрос {i}/{total}",
      question_n: "Вопрос {n}",
      question_alt: "Вопрос",
      answered_count: "Отвечено: {n}",
      left_count: "Осталось: {n}",
      correct_count: "Верно: {n}",
      wrong_count: "Ошибки: {n}",
      next: "Далее",
      finish: "Завершить",
      choose_answer: "Выберите ответ",
      hint_show: "Подсказка",
      hint_hide: "Скрыть подсказку",
      hint_empty: "Для этого вопроса подсказка отсутствует.",
      cannot_start_practice:
         "Не удалось запустить/возобновить сессию практики.",
      cannot_submit: "Не удалось отправить ответ.",
      session_closed_switch_local:
         "Серверная сессия закрыта/истекла. Перехожу в локальный режим.",

      // ✅ home tabs
      tickets_tab: "Билеты",
      categories_tab: "Темы",
      refresh: "Обновить",
      categories_loading: "Загрузка категорий…",
      categories_empty: "Категории отсутствуют.",
      categories_load_failed: "Не удалось загрузить категории.",
      in_progress_short: "В процессе",
      questions_count: "Вопросы",
      local_progress_note:
         "{name}: восстанавливаются локально сохраненные ответы из предыдущей попытки.",
      resume_ticket_title: "Продолжить билет {n}",
      reset_ticket: "Сбросить билет",
      reset_confirm: "Стереть ответы этого билета? Статистика сохранится.",
      reset_yes: "Да, сбросить",
      reset_no: "Нет",
      reset_failed: "Не удалось сбросить билет.",
      pass_aria: "Верно {ok}, ошибок {bad} из {total}",
      hard_tab: "Сложные вопросы",
      soon: "Скоро",
      progress_aria: "Зелёных {ok}, красных {bad} из {total} вопросов",
      question_stat_aria: "По этому вопросу: верно {ok} раз, ошибок {bad}",
   },
};

function formatI18n(str, vars) {
   if (!vars) return str;
   return str.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? `{${k}}`);
}

/* ===== Config din .env ===== */
const readEnv = (viteKey, craKey) =>
   (typeof import.meta !== "undefined" &&
      import.meta?.env &&
      import.meta.env[viteKey]) ||
   (typeof process !== "undefined" && process?.env && process.env[craKey]) ||
   "";

const START_ID = Number(
   readEnv("VITE_TICKETS_START", "REACT_APP_TICKETS_START") || 246,
);
const COUNT = Number(
   readEnv("VITE_TICKETS_COUNT", "REACT_APP_TICKETS_COUNT") || 269 - 246 + 1,
);
const TICKET_IDS = Array.from({ length: COUNT }, (_, i) => START_ID + i);

/* ===== Helpers ===== */
const IMG_HOST = "https://instruireauto.site";
function rewriteImageUrl(raw) {
   if (!raw) return null;
   try {
      const u = new URL(String(raw).trim(), IMG_HOST);
      const segs = u.pathname.split("/").filter(Boolean);
      const norm = (s) => s.toLowerCase().replace(/[-_]+/g, "");
      const token = "exameninstruireauto";
      let idx = segs.findIndex((p) => norm(p) === token);
      if (idx !== -1) segs[idx] = "images";
      else if (segs.length && norm(segs[0]) !== "images")
         segs.unshift("images");
      u.pathname = "/" + segs.join("/").replace(/\/{2,}/g, "/");
      return u.origin + u.pathname;
   } catch {
      const tail = String(raw)
         .trim()
         .replace(/^https?:\/\/[^/]+/i, "")
         .replace(/^\/+/, "");
      const m = tail.match(/EXAMEN[-_]?INSTRUIERE[-_]?AUTO\/(.+)/i);
      const tailClean = m ? m[1] : tail;
      return `${IMG_HOST.replace(/\/+$/, "")}/images/${tailClean}`.replace(
         /\/{2,}/g,
         "/",
      );
   }
}

/* pick text ro/ru (acceptă și string JSON {"ro":"","ru":""}) */
const pickLang = (v, lang) => {
   if (v == null) return "";
   if (typeof v === "string") {
      const s = v.trim();
      if (s.startsWith("{") && (s.includes('"ro"') || s.includes('"ru"'))) {
         try {
            const obj = JSON.parse(s);
            if (obj && typeof obj === "object") {
               return String(obj[lang] ?? obj.ro ?? obj.ru ?? "");
            }
         } catch {}
      }
      return s;
   }
   if (typeof v === "object") {
      if (v[lang] != null) return String(v[lang]);
      if (v.ro != null || v.ru != null) return String(v.ro ?? v.ru ?? "");
   }
   try {
      return String(v);
   } catch {
      return "";
   }
};

function pickQuestionHint(q, lang) {
   const nested = q?.question ?? q?.questionData ?? q?.details ?? null;
   const hintRaw =
      q?.hint ??
      q?.explanation ??
      q?.hintText ??
      nested?.hint ??
      nested?.explanation ??
      nested?.hintText ??
      "";
   const hintLocalized = pickLang(hintRaw, lang);
   const hintRo = pickLang(
      q?.hintRo ??
         q?.hint_ro ??
         nested?.hintRo ??
         nested?.hint_ro ??
         "",
      "ro",
   );
   const hintRu = pickLang(
      q?.hintRu ??
         q?.hint_ru ??
         nested?.hintRu ??
         nested?.hint_ru ??
         "",
      "ru",
   );
   if (lang === "ru") {
      return String(hintRu || hintRo || hintLocalized).trim();
   }
   return String(hintRo || hintRu || hintLocalized).trim();
}

/** Normalizează sesiunea -> ticket (ticket sau category) */
function normalizeSessionToTicket(sess, lang) {
   if (!sess) return null;

   const ticket = sess.ticket || {};
   const tidRaw = ticket?.id ?? sess.ticketId;
   const tid =
      Number.isInteger(Number(tidRaw)) && Number(tidRaw) > 0
         ? Number(tidRaw)
         : null;

   const name = pickLang(
      ticket?.name || sess.ticketName || (tid ? `Bilet ${tid}` : "Bilet"),
      lang,
   );

   const rawQs = ticket?.questions || sess.questions || [];

   const questions = (rawQs || []).map((q, i) => {
      const answersRaw = Array.isArray(q?.answers)
         ? q.answers
         : [q?.a1, q?.a2, q?.a3, q?.a4].filter((v) => v != null);

      const rawLocalId = q?.id ?? q?.questionId ?? i + 1;
      const parsedLocalId = Number(rawLocalId);
      const localId =
         Number.isInteger(parsedLocalId) && parsedLocalId > 0
            ? parsedLocalId
            : i + 1;

      const rawServerId = q?.questionId ?? q?.id ?? localId;
      const parsedServerId = Number(rawServerId);
      const serverQuestionId =
         Number.isInteger(parsedServerId) && parsedServerId > 0
            ? parsedServerId
            : localId;

      return {
         id: localId, // UI key
         serverQuestionId, // submit key
         text: pickLang(q?.text ?? q?.question ?? q?.title ?? "", lang),
         answers: answersRaw.map((a) => pickLang(a, lang)).filter(Boolean),
         image: rewriteImageUrl(pickLang(q?.image || q?.img || "", lang)),
         hint: pickQuestionHint(q, lang),
         order: Number.isFinite(q?.order) ? Number(q.order) : i,
         correctAnswer: q?.correctAnswer,
         // reluare: ce a ales deja elevul în trecerea curentă
         selectedAnswer: q?.selectedAnswer ?? null,
         isCorrect: typeof q?.isCorrect === "boolean" ? q.isCorrect : null,
         // de câte ori a răspuns elevul corect / greșit la întrebare (bilete + teme)
         stats: {
            correct: Math.max(0, Number(q?.stats?.correct) || 0),
            wrong: Math.max(0, Number(q?.stats?.wrong) || 0),
         },
      };
   });

   return { id: tid, name, questions };
}

const PRACTICE_ATTEMPT_FINISHED_EVENT = "practice:attempt-finished";
const PRACTICE_DRAFT_STORAGE_PREFIX = "student_practice_draft_v1";

/* ===== Helpers resume/hidratare ===== */
function isSessionOpenForAnswers(sess) {
   if (!sess || typeof sess !== "object") return false;

   const st = String(sess?.status || "").toUpperCase();
   if (st) return isInProgressStatus(st);

   if (sess?.completedAt || sess?.finishedAt || sess?.endedAt) return false;
   return true;
}

function isInProgressStatus(status) {
   const up = String(status || "").toUpperCase();
   return (
      up.includes("IN_PROGRESS") ||
      up.includes("STARTED") ||
      up.includes("ACTIVE")
   );
}

/**
 * Ciornele din localStorage serveau la „restaurarea” încercării anterioare la
 * „Înapoi”. Progresul stă acum pe server, așa că le ștergem (o singură dată).
 */
function clearLegacyPracticeDrafts() {
   try {
      if (typeof localStorage === "undefined") return;
      const stale = [];
      for (let i = 0; i < localStorage.length; i++) {
         const key = localStorage.key(i);
         if (key && key.startsWith(PRACTICE_DRAFT_STORAGE_PREFIX)) stale.push(key);
      }
      stale.forEach((key) => localStorage.removeItem(key));
   } catch {}
}

function buildInitialPracticeState({ sess, lang, fallbackName }) {
   let tkt = normalizeSessionToTicket(sess, lang);
   tkt = {
      ...tkt,
      name: fallbackName || tkt?.name || "Practica",
      questions: [...(tkt?.questions || [])].sort(
         (a, b) => (a.order ?? 0) - (b.order ?? 0),
      ),
   };

   // reluare: răspunsurile date revin, elevul continuă de la prima fără răspuns
   const { answersMap, baselineMap, idx } = buildResumeState(tkt.questions);

   const statsMap = {};
   for (const q of tkt.questions) statsMap[q.id] = q.stats;

   return {
      ticket: tkt,
      baselineMap,
      correctMap: buildPracticeCorrectMap(tkt),
      answersMap,
      statsMap,
      idx,
   };
}

function buildPracticeFinalScorePingPayload(
   ticket,
   answersMap,
   baselineMap,
   getServerQid,
) {
   const questions = Array.isArray(ticket?.questions) ? ticket.questions : [];

   for (let i = questions.length - 1; i >= 0; i -= 1) {
      const q = questions[i];
      const qKey = Number(q?.id);
      const serverQid = getServerQid(q);
      const answersLen = Array.isArray(q?.answers) ? q.answers.length : 0;

      if (!Number.isInteger(serverQid) || serverQid <= 0 || answersLen <= 0) {
         continue;
      }

      const localSelected = Number(answersMap?.[qKey]?.selected);
      const baselineSelected = normalizeSelectedToZeroBased(
         baselineMap?.[qKey] ?? baselineMap?.[serverQid],
         answersLen,
      );
      const selected = Number.isInteger(localSelected)
         ? localSelected
         : baselineSelected;

      if (!Number.isInteger(selected)) continue;

      return { questionId: serverQid, selectedAnswer: selected };
   }

   return null;
}

const getCatCount = (c) =>
   c?._count?.questions ??
   c?.questionCount ??
   c?.questionsCount ??
   c?.count ??
   c?.totalQuestions ??
   0;

function normalizePagedResponse(raw) {
   if (Array.isArray(raw)) return raw;
   const items =
      raw?.data ||
      raw?.items ||
      raw?.results ||
      raw?.rows ||
      raw?.categories ||
      [];
   return Array.isArray(items) ? items : [];
}

/** Fundalul biletului / temei ca bară: --ok = cât e verde, --bad = unde se termină roșul (în %). */
function progressFill(info) {
   const total = Number(info?.total) || 0;
   if (total <= 0) return undefined;
   const ok = Math.max(0, Math.min(100, (100 * (Number(info.green) || 0)) / total));
   const bad = Math.max(ok, Math.min(100, ok + (100 * (Number(info.red) || 0)) / total));
   return { "--ok": `${ok}%`, "--bad": `${bad}%` };
}

function catTitleByLang(cat, lang) {
   const ro = String(cat?.nameRo ?? "").trim();
   const ru = String(cat?.nameRu ?? "").trim();
   if (lang === "ru") return ru || ro || `#${cat?.id}`;
   return ro || ru || `#${cat?.id}`;
}

function normalizeDisplayText(value) {
   return String(value || "")
      .replace(/[\r\n]+/g, " ")
      .replace(/\s+/g, " ")
      .replace(/^["'“”„]+|["'“”„]+$/g, "")
      .trim();
}

function toSentenceCase(value, lang = "ro") {
   const clean = normalizeDisplayText(value);
   if (!clean) return "";

   const locale = lang === "ru" ? "ru-RU" : "ro-RO";
   let normalized = clean.toLocaleLowerCase(locale);

   normalized = normalized.replace(
      /(^|[\s(])([a-zа-яăâîșț])\./giu,
      (_, prefix, letter) => `${prefix}${letter.toLocaleUpperCase(locale)}.`,
   );

   normalized = normalized.replace(
      /(^|[.!?]\s+|:\s+|\(\s*)([a-zа-яăâîșț])/giu,
      (_, prefix, letter) => `${prefix}${letter.toLocaleUpperCase(locale)}`,
   );

   return normalized;
}

function buildCategoryTitleParts(title, lang = "ro") {
   const clean = normalizeDisplayText(title);
   if (!clean) return [];

   const markerMatch = clean.match(
      /^\s*((?:\d+\.\d+(?:\.\d+)*\.?|\d+\.))\s*(.*)$/u,
   );

   if (!markerMatch) {
      return [{ marker: null, text: toSentenceCase(clean, lang) }];
   }

   const rawMarker = String(markerMatch[1] || "")
      .replace(/\s+/g, "")
      .trim();
   const marker = rawMarker
      ? rawMarker.endsWith(".")
         ? rawMarker
         : `${rawMarker}.`
      : null;
   const text = toSentenceCase(markerMatch[2], lang);

   return [{ marker, text: text || null }].filter(
      (part) => part.marker || part.text,
   );
}

function getResolvedSelectedIdx(question, answersMap, baselineMap) {
   const qid = Number(question?.id);
   const serverQid = Number(question?.serverQuestionId);

   if (Number.isInteger(qid) && Number.isInteger(answersMap?.[qid]?.selected)) {
      return Number(answersMap[qid].selected);
   }

   const rawBaseline =
      (Number.isInteger(qid) ? baselineMap?.[qid] : null) ??
      (Number.isInteger(serverQid) ? baselineMap?.[serverQid] : null);

   return normalizeSelectedToZeroBased(
      rawBaseline,
      Array.isArray(question?.answers) ? question.answers.length : 0,
   );
}

function getResolvedCorrectIdx(question, answersMap, correctMap) {
   const qid = Number(question?.id);
   const serverQid = Number(question?.serverQuestionId);

   if (
      Number.isInteger(qid) &&
      Number.isInteger(answersMap?.[qid]?.correctIdx)
   ) {
      return Number(answersMap[qid].correctIdx);
   }
   if (Number.isInteger(qid) && Number.isInteger(correctMap?.[qid])) {
      return Number(correctMap[qid]);
   }
   if (
      Number.isInteger(serverQid) &&
      Number.isInteger(correctMap?.[serverQid])
   ) {
      return Number(correctMap[serverQid]);
   }
   return null;
}

function summarizeTicketAttempt(ticket, answersMap, baselineMap, correctMap) {
   const questions = Array.isArray(ticket?.questions) ? ticket.questions : [];
   let correct = 0;
   let answered = 0;

   for (const q of questions) {
      const selected = getResolvedSelectedIdx(q, answersMap, baselineMap);
      if (!Number.isInteger(selected)) continue;

      answered += 1;

      const qid = Number(q?.id);
      const explicitCorrect =
         Number.isInteger(qid) &&
         typeof answersMap?.[qid]?.correct === "boolean"
            ? answersMap[qid].correct
            : null;

      const correctIdx = getResolvedCorrectIdx(q, answersMap, correctMap);
      const isCorrect =
         explicitCorrect !== null
            ? explicitCorrect
            : Number.isInteger(correctIdx)
              ? Number(selected) === Number(correctIdx)
              : false;

      if (isCorrect) correct += 1;
   }

   const total = questions.length;
   const unanswered = Math.max(0, total - answered);
   const wrong = Math.max(0, answered - correct);

   return {
      total,
      correct,
      wrong,
      unanswered,
      answered,
      allCorrect: total > 0 && correct === total,
   };
}

export default function Practice({ onViewChange }) {
   const [view, setView] = useState("tickets");
   // pagina (SPTest) ascunde Statistica în timpul testului
   useEffect(() => {
      onViewChange?.(view);
   }, [view, onViewChange]);
   const [homeTab, setHomeTab] = useState("tickets");

   const [sessionKind, setSessionKind] = useState("ticket"); // 'ticket' | 'category'

   const DISPLAY_BASE = START_ID - 1;
   const tickets = useMemo(
      () => TICKET_IDS.map((id) => ({ id, nr: id - DISPLAY_BASE })),
      [DISPLAY_BASE],
   );

   const [lang, setLang] = useState(() => {
      const saved =
         (typeof localStorage !== "undefined" &&
            localStorage.getItem("exam.lang")) ||
         "ro";
      return saved === "ru" ? "ru" : "ro";
   });

   useEffect(() => {
      try {
         localStorage.setItem("exam.lang", lang);
      } catch {}
      window.dispatchEvent(new CustomEvent("exam:lang", { detail: lang }));
   }, [lang]);

   const t = useCallback(
      (key, vars) => {
         const base = (I18N[lang] && I18N[lang][key]) || I18N.ro[key] || key;
         return formatI18n(base, vars);
      },
      [lang],
   );

   // { [ticketId]: { questionCount, inProgress, lastFinished } } — un singur apel
   const [ticketOverviewMap, setTicketOverviewMap] = useState({});
   const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
   // statistica pe întrebări: temele (un singur apel pentru toate)
   const [practiceStats, setPracticeStats] = useState(null);
   // { [questionId]: { correct, wrong } } — bara de sub imagine
   const [questionStatsMap, setQuestionStatsMap] = useState({});

   const [practiceId, setPracticeId] = useState(null);
   const [localAttemptId, setLocalAttemptId] = useState(null);
   const [practiceMode, setPracticeMode] = useState("server"); // 'server' | 'local'

   const [ticketId, setTicketId] = useState(null);
   const [ticket, setTicket] = useState(null);

   const [answersMap, setAnswersMap] = useState({});
   const [baselineMap, setBaselineMap] = useState({});
   const [correctMap, setCorrectMap] = useState({});
   const [correctLoaded, setCorrectLoaded] = useState(false);

   const [idx, setIdx] = useState(0);
   const [hintOpenByQuestionId, setHintOpenByQuestionId] = useState({});
   const [loading, setLoading] = useState(false);
   const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
   const [resetting, setResetting] = useState(false);

   const [pillMsgs, setPillMsgs] = useState([]);
   const qTextRef = useRef(null);
   const timerRef = useRef(null);
   const practiceIdRef = useRef(null);
   const [remaining, setRemaining] = useState(null);
   // Biletele și temele sunt fără limită de timp pentru student.
   const sessionTimeLimitSec =
      sessionKind === "ticket" || sessionKind === "category" ? null : 20 * 60;
   const hasSessionTimeLimit =
      Number.isInteger(sessionTimeLimitSec) && sessionTimeLimitSec > 0;

   const pushError = (text) =>
      setPillMsgs((arr) => [
         ...arr,
         { id: Date.now() + Math.random(), type: "error", text },
      ]);

   const dismissLastPill = () => setPillMsgs((arr) => arr.slice(0, -1));
   useEffect(() => {
      if (!pillMsgs.length) return;
      const tmo = setTimeout(dismissLastPill, 4000);
      return () => clearTimeout(tmo);
   }, [pillMsgs]);

   useEffect(() => {
      clearLegacyPracticeDrafts();
   }, []);

   useEffect(() => {
      return () => timerRef.current && clearInterval(timerRef.current);
   }, []);

   useEffect(() => {
      practiceIdRef.current =
         Number.isInteger(Number(practiceId)) && Number(practiceId) > 0
            ? Number(practiceId)
            : null;
   }, [practiceId]);

   useEffect(() => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (!hasSessionTimeLimit) {
         setRemaining(null);
         return;
      }

      setRemaining(sessionTimeLimitSec);
      if (!practiceId && !localAttemptId) return;

      timerRef.current = setInterval(() => {
         setRemaining((r) => (r <= 1 ? 0 : r - 1));
      }, 1000);
   }, [
      practiceId,
      localAttemptId,
      practiceMode,
      hasSessionTimeLimit,
      sessionTimeLimitSec,
   ]);

   useEffect(() => {
      if (!hasSessionTimeLimit) return;
      if (remaining === 0 && (practiceId || localAttemptId)) {
         (async () => {
            await finalizeAttempt("timeout");
         })();
      }
   }, [remaining, practiceId, localAttemptId, hasSessionTimeLimit]); // eslint-disable-line

   const scrollToCurrent = () => {
      const el = qTextRef.current;
      if (!el) return;
      const offset = getStudentQuestionScrollOffset();
      const top = Math.max(
         0,
         window.scrollY + (el.getBoundingClientRect().top || 0) - offset,
      );
      if (Math.abs(window.scrollY - top) > 1) {
         window.scrollTo({ top, behavior: "smooth" });
      }
   };

   useEffect(() => {
      if (!ticket) return;
      const raf = requestAnimationFrame(() =>
         requestAnimationFrame(scrollToCurrent),
      );
      return () => cancelAnimationFrame(raf);
   }, [ticket, idx]);

   /* ====== Lista de bilete: întrebări + progres, într-un singur apel ====== */
   useEffect(() => {
      if (view !== "tickets") return;
      let alive = true;

      getPracticeTicketsOverview(tickets.map((tkt) => tkt.id))
         .then((rows) => {
            if (!alive) return;
            const next = {};
            for (const row of rows) {
               const tid = Number(row?.ticketId);
               if (Number.isInteger(tid) && tid > 0) next[tid] = row;
            }
            setTicketOverviewMap(next);
         })
         .catch(() => {
            // cardurile rămân fără scor, dar biletele se pot deschide
         });

      return () => {
         alive = false;
      };
   }, [view, tickets, historyRefreshKey]);

   /* ====== Statistica pe întrebări (temele) ====== */
   useEffect(() => {
      if (view !== "tickets") return;
      let alive = true;

      getPracticeQuestionStats(tickets.map((tkt) => tkt.id))
         .then((data) => {
            if (alive) setPracticeStats(data || null);
         })
         .catch(() => {
            // temele rămân fără scor, dar se pot deschide
         });

      return () => {
         alive = false;
      };
   }, [view, tickets, historyRefreshKey]);

   /* ====== Culoarea și scorul biletelor ====== */
   // verde: toate întrebările verzi (pentru totdeauna); roșu: are întrebări roșii
   const ticketCardInfoMap = useMemo(() => {
      const infoMap = {};

      for (const tkt of tickets) {
         const tid = Number(tkt?.id);
         const row = ticketOverviewMap?.[tid];
         if (!row) continue;

         const progress = row.progress || {};
         const total = Math.max(0, Number(progress.total ?? row.questionCount) || 0);
         const clamp = (n) => Math.max(0, Math.min(total, Number(n) || 0));
         const green = clamp(progress.green);
         const red = clamp(progress.red);

         infoMap[tid] = {
            total,
            green,
            red,
            hasInProgress: !!row.inProgress,
            state: progress.passed ? "ok" : red > 0 ? "bad" : null,
         };
      }

      return infoMap;
   }, [ticketOverviewMap, tickets]);

   /* =========================
      CATEGORIES (HOME TAB)
  ========================= */
   const [categories, setCategories] = useState([]);
   const [catLoading, setCatLoading] = useState(false);
   const [catError, setCatError] = useState("");

   // aceleași întrebări ca la bilete: tema e verde când toate sunt verzi
   const catStatsMap = useMemo(() => {
      const map = {};
      for (const row of practiceStats?.categories || []) {
         const cid = Number(row?.categoryId);
         if (!Number.isInteger(cid)) continue;
         map[cid] = {
            total: Number(row.total) || 0,
            green: Number(row.green) || 0,
            red: Number(row.red) || 0,
            state: row.passed ? "ok" : Number(row.red) > 0 ? "bad" : "none",
         };
      }
      return map;
   }, [practiceStats]);
   const catStatsLoading = practiceStats === null;

   const loadCategories = useCallback(async () => {
      setCatLoading(true);
      setCatError("");
      try {
         let data;
         try {
            data = await getQuestionCategoriesWithCount();
         } catch {
            const raw = await getQuestionCategories(1, 2000);
            data = normalizePagedResponse(raw);
         }
         const list = Array.isArray(data) ? data : normalizePagedResponse(data);
         const sortedList = [...list].sort((a, b) => {
            const aId = Number(a?.id);
            const bId = Number(b?.id);
            const aOk = Number.isFinite(aId);
            const bOk = Number.isFinite(bId);
            if (aOk && bOk) return aId - bId;
            if (aOk) return -1;
            if (bOk) return 1;
            return 0;
         });

         setCategories(sortedList);
      } catch (e) {
         setCategories([]);
         setCatError(e?.message || t("categories_load_failed"));
      } finally {
         setCatLoading(false);
      }
   }, [t]);

   useEffect(() => {
      if (view !== "tickets") return;
      if (homeTab !== "categories") return;
      if (categories.length) return;
      loadCategories();
   }, [view, homeTab, categories.length, loadCategories]);

   const getServerQid = useCallback((q) => {
      return Number(q?.serverQuestionId ?? q?.questionId ?? q?.id);
   }, []);

   const enterTicket = async (tid) => {
      setSessionKind("ticket");

      setLoading(true);

      setPracticeId(null);
      setLocalAttemptId(null);
      setPracticeMode("server");
      setTicket(null);
      setTicketId(null);
      setAnswersMap({});
      setBaselineMap({});
      setCorrectMap({});
      setCorrectLoaded(false);
      setIdx(0);
      setHintOpenByQuestionId({});
      setResetConfirmOpen(false);

      try {
         // dacă biletul e început, serverul întoarce aceeași încercare
         const started = await startPracticeSession(tid, lang);
         const pid =
            Number(started?.id) ||
            Number(started?.practiceId) ||
            Number(started?.sessionId);
         if (!Number.isInteger(pid) || pid <= 0) {
            throw new Error("PracticeID invalid la start.");
         }

         const serverTid = Number(started?.ticketId);
         const effectiveTid =
            Number.isInteger(serverTid) && serverTid > 0
               ? serverTid
               : Number(tid);

         // pornirea întoarce deja sesiunea completă; o cerem din nou doar dacă lipsește
         const sess = Array.isArray(started?.questions)
            ? started
            : await getPracticeSession(pid, lang);

         setPracticeMode("server");
         setPracticeId(pid);
         setTicketId(effectiveTid);

         const initial = buildInitialPracticeState({
            sess,
            lang,
         });

         setTicket(initial.ticket);
         setBaselineMap(initial.baselineMap);
         setCorrectMap(initial.correctMap);
         setCorrectLoaded(true);
         setAnswersMap(initial.answersMap);
         setQuestionStatsMap(initial.statsMap);
         setIdx(initial.idx);
         setView("test");
      } catch (e) {
         setCorrectLoaded(true);
         setCorrectMap({});
         pushError(t("cannot_start_practice"));
      } finally {
         setLoading(false);
      }
   };

   const enterCategory = async (cat) => {
      setSessionKind("category");

      const cid = Number(cat?.id);
      if (!Number.isInteger(cid) || cid <= 0) return;

      const title = catTitleByLang(cat, lang);

      const count = Number(getCatCount(cat) || 0);
      const questionCount = Number.isInteger(count) && count > 0 ? count : 20;

      setLoading(true);

      setPracticeId(null);
      setLocalAttemptId(null);
      setPracticeMode("server");
      setTicket(null);
      setTicketId(null);

      setAnswersMap({});
      setBaselineMap({});
      setCorrectMap({});
      setCorrectLoaded(false);
      setIdx(0);
      setHintOpenByQuestionId({});
      setResetConfirmOpen(false);

      try {
         const started = await startPracticeSessionByCategory({
            categoryId: cid,
            questionCount,
            lang,
         });

         const pid =
            Number(started?.id) ||
            Number(started?.practiceId) ||
            Number(started?.sessionId);

         if (!Number.isInteger(pid) || pid <= 0) {
            throw new Error(
               "PracticeID invalid la startPracticeSessionByCategory.",
            );
         }

         // pornirea întoarce deja sesiunea completă; o cerem din nou doar dacă lipsește
         const sess = Array.isArray(started?.questions)
            ? started
            : await getPracticeSession(pid, lang);

         setPracticeMode("server");
         setPracticeId(pid);
         const initial = buildInitialPracticeState({
            sess,
            lang,
            fallbackName: title || "Categorie",
         });

         setTicket(initial.ticket);
         setBaselineMap(initial.baselineMap);
         setCorrectMap(initial.correctMap);
         setCorrectLoaded(true);
         setAnswersMap(initial.answersMap);
         setQuestionStatsMap(initial.statsMap);
         setIdx(initial.idx);
         setView("test");
      } catch (e) {
         setCorrectLoaded(true);
         setCorrectMap({});
         pushError(t("cannot_start_practice"));
      } finally {
         setLoading(false);
      }
   };

   const current = ticket?.questions?.[idx] || null;

   // imaginea întrebării următoare se descarcă din timp
   const nextImage = ticket?.questions?.[idx + 1]?.image || null;
   useEffect(() => {
      if (!nextImage) return;
      const img = new Image();
      img.src = nextImage;
   }, [nextImage]);

   const total = ticket?.questions?.length || 0;
   const currentStats = (current && questionStatsMap[current.id]) || {
      correct: 0,
      wrong: 0,
   };
   const currentHintText = String(current?.hint || "").trim();
   const isCurrentHintOpen = current
      ? hintOpenByQuestionId[current.id] === true
      : false;
   const toggleCurrentHint = useCallback(() => {
      if (!current) return;
      setHintOpenByQuestionId((prev) => ({
         ...prev,
         [current.id]: !prev[current.id],
      }));
   }, [current]);

   useEffect(() => {
      if (!correctLoaded || !ticket) return;

      setAnswersMap((prev) => {
         let changed = false;
         const next = { ...prev };

         for (const q of ticket.questions || []) {
            const qid = q.id;
            const sel = next[qid]?.selected;
            if (sel != null) {
               const ci = Number.isInteger(correctMap[qid])
                  ? correctMap[qid]
                  : null;
               const corr =
                  Number.isInteger(ci) && Number.isInteger(sel)
                     ? Number(sel) === Number(ci)
                     : null;

               if (
                  next[qid]?.correct !== corr ||
                  next[qid]?.correctIdx !== ci
               ) {
                  next[qid] = {
                     ...next[qid],
                     correct: corr,
                     correctIdx: Number.isInteger(ci) ? ci : null,
                  };
                  changed = true;
               }
            }
         }

         return changed ? next : prev;
      });
   }, [correctLoaded, correctMap, ticket, sessionKind]);

   const metrics = useMemo(() => {
      let ok = 0,
         bad = 0,
         answered = 0;
      Object.values(answersMap).forEach((a) => {
         if (a?.selected != null) answered++;
         if (a?.correct === true) ok++;
         else if (a?.correct === false) bad++;
      });
      return { ok, bad, answered, left: Math.max(0, total - answered) };
   }, [answersMap, total]);

   const hasPendingSync = useMemo(
      () => Object.values(answersMap).some((a) => a?.pending === true),
      [answersMap],
   );

   const allAnsweredNow = useMemo(() => {
      if (!ticket?.questions?.length) return false;
      return ticket.questions.every((q) => {
         const a = answersMap[q.id];
         return a && a.selected != null;
      });
   }, [ticket, answersMap]);

   const jumpTo = (i) => {
      if (!ticket) return;
      const clamped = Math.max(0, Math.min(i, total - 1));
      setIdx(clamped);
   };

   const goNext = () => {
      if (!ticket) return;
      setIdx(Math.min(idx + 1, total - 1));
   };

   const collectPendingPracticeDiffs = useCallback(() => {
      const diffs = [];

      for (const q of ticket?.questions || []) {
         const qKey = Number(q.id);
         const answersLen = Array.isArray(q?.answers) ? q.answers.length : 0;
         const pendingSel =
            answersMap[qKey] && Number.isInteger(answersMap[qKey].selected)
               ? Number(answersMap[qKey].selected)
               : null;
         if (pendingSel == null) continue;

         const baselineSel = normalizeSelectedToZeroBased(
            baselineMap[qKey],
            answersLen,
         );

         if (baselineSel === null || pendingSel !== baselineSel) {
            const serverQid = getServerQid(q);
            if (Number.isInteger(serverQid) && serverQid > 0) {
               diffs.push({
                  questionId: serverQid,
                  selectedAnswer: pendingSel,
               });
            }
         }
      }

      return diffs;
   }, [answersMap, baselineMap, getServerQid, ticket]);

   const submitPracticeDiffsFast = useCallback(
      async (kind, diffs, finalScore = null) => {
         if (!practiceId) return;

         const safeKind = kind === "category" ? "category" : "ticket";
         if (!diffs.length) {
            if (Number.isFinite(Number(finalScore))) {
               const scorePingPayload = buildPracticeFinalScorePingPayload(
                  ticket,
                  answersMap,
                  baselineMap,
                  getServerQid,
               );
               if (scorePingPayload) {
                  try {
                     await submitPracticeAnswer(practiceId, {
                        ...scorePingPayload,
                        score: Number(finalScore),
                     });
                  } catch {}
               }
            }
            return;
         }

         const withLogging = async (payload, index, totalCount, isLast) => {
            try {
               await submitPracticeAnswer(practiceId, payload);
            } catch {
               // ignore punctual
            }
         };

         const totalCount = diffs.length;
         const trailing = diffs[totalCount - 1];
         const leading = diffs.slice(0, -1);
         const CHUNK_SIZE = 6;

         for (let start = 0; start < leading.length; start += CHUNK_SIZE) {
            const chunk = leading.slice(start, start + CHUNK_SIZE);
            await Promise.allSettled(
               chunk.map((diff, offset) =>
                  withLogging(diff, start + offset, totalCount, false),
               ),
            );
         }

         const finalPayload = {
            ...trailing,
            ...(Number.isFinite(Number(finalScore))
               ? { score: Number(finalScore) }
               : {}),
         };

         await withLogging(finalPayload, totalCount - 1, totalCount, true);
      },
      [answersMap, baselineMap, getServerQid, practiceId, sessionKind, ticket],
   );

   const syncPracticeAnswerNow = useCallback(
      async (question, selected, fallbackCorrectIdx = null) => {
         const qKey = Number(question?.id);
         const serverQid = getServerQid(question);
         const answersLen = Array.isArray(question?.answers)
            ? question.answers.length
            : 0;
         const activePracticeId =
            Number.isInteger(Number(practiceId)) && Number(practiceId) > 0
               ? Number(practiceId)
               : null;

         if (
            !Number.isInteger(qKey) ||
            qKey <= 0 ||
            !Number.isInteger(selected) ||
            selected < 0
         ) {
            return;
         }

         if (
            practiceMode !== "server" ||
            !activePracticeId ||
            !Number.isInteger(serverQid) ||
            serverQid <= 0
         ) {
            setAnswersMap((prev) => {
               const currentEntry = prev[qKey];
               if (!currentEntry || currentEntry.selected !== selected) {
                  return prev;
               }
               return {
                  ...prev,
                  [qKey]: {
                     ...currentEntry,
                     pending: false,
                  },
               };
            });
            return;
         }

         try {
            const normalizeResp = await submitPracticeAnswer(activePracticeId, {
               questionId: serverQid,
               selectedAnswer: selected,
            });
            const normalized =
               typeof normalizeResp === "function"
                  ? normalizeResp(answersLen)
                  : { correct: null, correctIdx: null };

            const resolvedCorrectIdx = Number.isInteger(normalized?.correctIdx)
               ? Number(normalized.correctIdx)
               : Number.isInteger(fallbackCorrectIdx)
                 ? Number(fallbackCorrectIdx)
                 : null;

            const resolvedCorrect =
               typeof normalized?.correct === "boolean"
                  ? normalized.correct
                  : Number.isInteger(resolvedCorrectIdx)
                    ? Number(selected) === Number(resolvedCorrectIdx)
                    : null;

            if (practiceIdRef.current !== activePracticeId) return;

            // statistica întrebării după acest răspuns (bara de sub imagine)
            const serverStats = normalized?._raw?.questionStats;
            if (serverStats && typeof serverStats === "object") {
               setQuestionStatsMap((prev) => ({
                  ...prev,
                  [qKey]: {
                     correct: Math.max(0, Number(serverStats.correct) || 0),
                     wrong: Math.max(0, Number(serverStats.wrong) || 0),
                  },
               }));
            }

            setBaselineMap((prev) => ({
               ...prev,
               [qKey]: selected,
               ...(serverQid !== qKey ? { [serverQid]: selected } : {}),
            }));

            if (Number.isInteger(resolvedCorrectIdx)) {
               setCorrectMap((prev) => ({
                  ...prev,
                  [qKey]: resolvedCorrectIdx,
                  ...(serverQid !== qKey
                     ? { [serverQid]: resolvedCorrectIdx }
                     : {}),
               }));
            }

            setAnswersMap((prev) => {
               const currentEntry = prev[qKey];
               if (!currentEntry || currentEntry.selected !== selected) {
                  return prev;
               }
               return {
                  ...prev,
                  [qKey]: {
                     ...currentEntry,
                     correct: resolvedCorrect,
                     correctIdx: Number.isInteger(resolvedCorrectIdx)
                        ? resolvedCorrectIdx
                        : null,
                     pending: false,
                     syncError: false,
                  },
               };
            });
         } catch {
            if (practiceIdRef.current !== activePracticeId) return;
            setAnswersMap((prev) => {
               const currentEntry = prev[qKey];
               if (!currentEntry || currentEntry.selected !== selected) {
                  return prev;
               }
               return {
                  ...prev,
                  [qKey]: {
                     ...currentEntry,
                     pending: false,
                     syncError: true,
                  },
               };
            });
         }
      },
      [getServerQid, practiceId, practiceMode],
   );

   const onChooseTicket = (answerIdx) => {
      if (!current) return;

      const qKey = Number(current.id);
      if (!Number.isInteger(qKey) || qKey <= 0) return;

      if (answersMap[qKey]?.selected != null) return;

      const selected = Number(answerIdx);

      const serverQid = getServerQid(current);
      const ci = Number.isInteger(correctMap[qKey])
         ? Number(correctMap[qKey])
         : Number.isInteger(correctMap[serverQid])
           ? Number(correctMap[serverQid])
           : null;
      const corr =
         Number.isInteger(ci) && Number.isInteger(selected)
            ? Number(selected) === Number(ci)
            : null;

      setAnswersMap((prevMap) => ({
         ...prevMap,
         [qKey]: {
            selected,
            correct: corr,
            correctIdx: Number.isInteger(ci) ? ci : null,
            at: new Date().toISOString(),
            pending: true,
            syncError: false,
         },
      }));

      void syncPracticeAnswerNow(current, selected, ci);
   };

   const handleCategoryAnswerLocal = (answerIdx) => {
      if (!current) return;
      if (loading) return;

      const qKey = Number(current.id);
      if (!Number.isInteger(qKey) || qKey <= 0) return;

      const already = answersMap[qKey];
      if (already?.selected != null || already?.pending) return;

      const selected = Number(answerIdx);
      const serverQid = getServerQid(current);
      const ci = Number.isInteger(correctMap[qKey])
         ? Number(correctMap[qKey])
         : Number.isInteger(correctMap[serverQid])
           ? Number(correctMap[serverQid])
           : null;
      const hasLocalCorrect = Number.isInteger(ci);

      setAnswersMap((prev) => ({
         ...prev,
         [qKey]: {
            selected,
            correct: hasLocalCorrect ? Number(selected) === Number(ci) : null,
            correctIdx: hasLocalCorrect ? ci : null,
            at: new Date().toISOString(),
            pending: true,
            syncError: false,
         },
      }));

      void syncPracticeAnswerNow(current, selected, ci);
   };

   // Ticket flow: submit la final, 0-based (aliniat cu scorul local/corectIdx).
   async function sendPendingTicketDiffs(finalScore = null) {
      if (sessionKind !== "ticket") return;
      if (practiceMode !== "server" || !practiceId) return;
      if (!ticket?.questions?.length) return;

      try {
         const sess = await getPracticeSession(practiceId, lang);
         if (!isSessionOpenForAnswers(sess)) return;
      } catch {
         return;
      }

      const diffs = collectPendingPracticeDiffs();
      await submitPracticeDiffsFast("ticket", diffs, finalScore);
   }

   // Category flow (separat): submit la final, 0-based.
   async function sendPendingCategoryDiffs(finalScore = null) {
      if (sessionKind !== "category") return;
      if (practiceMode !== "server" || !practiceId) return;
      if (!ticket?.questions?.length) return;

      try {
         const sess = await getPracticeSession(practiceId, lang);
         if (!isSessionOpenForAnswers(sess)) return;
      } catch {
         return;
      }

      const diffs = collectPendingPracticeDiffs();
      await submitPracticeDiffsFast("category", diffs, finalScore);
   }

   // Ieșirea (Înapoi / Finalizează) nu mai restaurează nimic: progresul rămâne
   // pe server și biletul se continuă de unde a rămas.
   const finalizeAttempt = async (reason = "user-finish") => {
      const finalTicketId = Number(ticketId ?? ticket?.id);
      const finalSummary = summarizeTicketAttempt(
         ticket,
         answersMap,
         baselineMap,
         correctMap,
      );

      // răspunsurile rămase netrimise (de ex. rețea căzută) pleacă acum
      try {
         const finalScoreToSend =
            finalSummary.total > 0 ? Number(finalSummary.correct) : null;
         if (sessionKind === "ticket") {
            await sendPendingTicketDiffs(finalScoreToSend);
         } else if (sessionKind === "category") {
            await sendPendingCategoryDiffs(finalScoreToSend);
         }
      } catch {}

      // mic "sync" pentru ca serverul să finalizeze statusul înainte de history
      if (practiceMode === "server" && practiceId) {
         try {
            await getPracticeSession(practiceId, lang);
         } catch {}
      }

      if (practiceMode === "server" && finalSummary.total > 0) {
         try {
            window.dispatchEvent(
               new CustomEvent(PRACTICE_ATTEMPT_FINISHED_EVENT, {
                  detail: {
                     sessionKind,
                     reason,
                     ticketId:
                        sessionKind === "ticket" &&
                        Number.isInteger(finalTicketId) &&
                        finalTicketId > 0
                           ? finalTicketId
                           : null,
                  },
               }),
            );
         } catch {}
      }
      setHistoryRefreshKey((prev) => prev + 1);

      setView("tickets");
      setPracticeId(null);
      setLocalAttemptId(null);
      setPracticeMode("server");
      setSessionKind("ticket");
      setTicket(null);
      setTicketId(null);
      setAnswersMap({});
      setBaselineMap({});
      setCorrectMap({});
      setCorrectLoaded(false);
      setIdx(0);
      setHintOpenByQuestionId({});
      setResetConfirmOpen(false);
      setQuestionStatsMap({});
   };

   // Resetarea biletului: răspunsurile trecerii curente se golesc și biletul
   // reîncepe de la prima întrebare. Statistica cumulată rămâne pe server.
   const canResetTicket =
      sessionKind === "ticket" && practiceMode === "server" && !!practiceId;

   const resetCurrentTicket = async () => {
      if (!canResetTicket || resetting) return;
      setResetting(true);
      try {
         const sess = await resetPracticeSession(practiceId, lang);
         const initial = buildInitialPracticeState({ sess, lang });
         setTicket(initial.ticket);
         setBaselineMap(initial.baselineMap);
         setCorrectMap(initial.correctMap);
         setAnswersMap(initial.answersMap);
         setQuestionStatsMap(initial.statsMap);
         setIdx(initial.idx);
         setHintOpenByQuestionId({});
         setHistoryRefreshKey((prev) => prev + 1);
      } catch {
         pushError(t("reset_failed"));
      } finally {
         setResetting(false);
         setResetConfirmOpen(false);
      }
   };

   const statusBoard = useMemo(() => {
      if (!ticket) return [];
      return ticket.questions.map((q, i) => {
         const a = answersMap[q.id];
         let status = "none";
         if (a?.selected != null)
            status =
               a.correct === false ? "bad" : a.correct === true ? "ok" : "wait";
         return { i, status };
      });
   }, [ticket, answersMap]);

   // pe telefon numerele întrebărilor sunt o bandă care derulează: întrebarea curentă rămâne la vedere
   useEffect(() => {
      if (view !== "test") return;
      const board = document.querySelector(".student-test__statusboard");
      const dot = board?.querySelector(".student-test__dot--current");
      if (!board || !dot || board.scrollWidth <= board.clientWidth) return;
      const left = dot.offsetLeft - (board.clientWidth - dot.offsetWidth) / 2;
      board.scrollTo({ left: Math.max(0, left), behavior: "smooth" });
   }, [view, idx]);

   const keySalt = practiceMode === "local" ? localAttemptId : practiceId;
   const practiceRootClassName =
      view === "test"
         ? [
              "student-test",
              sessionKind === "category"
                 ? "student-test--category"
                 : "student-test--ticket",
           ].join(" ")
         : "student-practice";

   return (
      <div className={practiceRootClassName}>
         <AlertPills messages={pillMsgs} onDismiss={dismissLastPill} />

         {view === "tickets" && (
            <>
               {/* rândul comun de sus (.pageToolbar), ca la calendar */}
               <div className="pageToolbar sTest__toolbar">
                  <h1 className="sTest__title">{t("practice_title")}</h1>
                  <div className="pageToolbar__controls sTest__controls">
                     <ChoiceUI
                        look="segmented"
                        aria-label={t("practice_title")}
                        className="sTest__tabs"
                        value={homeTab}
                        onChange={(v) => {
                           setHomeTab(v);
                           if (v === "categories" && !categories.length) loadCategories();
                        }}
                        options={[
                           { value: "tickets", label: t("tickets_tab") },
                           { value: "categories", label: t("categories_tab") },
                        ]}
                     />
                     <ChoiceUI
                        look="segmented"
                        aria-label={t("lang_ro") + " / " + t("lang_ru")}
                        className="sTest__lang"
                        value={lang}
                        onChange={setLang}
                        options={[
                           { value: "ro", label: "RO", title: t("lang_ro") },
                           { value: "ru", label: "RU", title: t("lang_ru") },
                        ]}
                     />
                  </div>
               </div>

               {homeTab === "categories" && (
                  <div className="student-practice__cat-list">
                     {catError ? (
                        <div className="student-practice__cat-empty">
                           {catError}
                        </div>
                     ) : catLoading ? (
                        <div className="student-practice__cat-empty">
                           {t("categories_loading")}
                        </div>
                     ) : (categories || []).length === 0 ? (
                        <div className="student-practice__cat-empty">
                           {t("categories_empty")}
                        </div>
                     ) : (
                        (categories || []).map((c) => {
                           const title = catTitleByLang(c, lang);
                           const titleParts = buildCategoryTitleParts(
                              title,
                              lang,
                           );

                           const stat = catStatsMap[c.id];
                           const state = stat?.state || "none";
                           const catScoreText =
                              Number(stat?.total) > 0
                                 ? `${stat.green}/${stat.total}`
                                 : null;

                           return (
                              <button
                                 key={c.id}
                                 className={
                                    "student-practice__cat-item" +
                                    (state === "ok"
                                       ? " student-practice__cat-item--ok"
                                       : "") +
                                    (state === "bad"
                                       ? " student-practice__cat-item--bad"
                                       : "") +
                                    (state === "none"
                                       ? " student-practice__cat-item--none"
                                       : "")
                                 }
                                 style={progressFill(stat)}
                                 onClick={() => enterCategory(c)}
                                 disabled={loading}
                                 title={title}
                              >
                                 <div className="student-practice__cat-left">
                                    <div className="student-practice__cat-title">
                                       {(titleParts.length
                                          ? titleParts
                                          : [{ marker: null, text: title }]
                                       ).map((part, index) => (
                                          <span
                                             key={`${c.id}-${index}`}
                                             className="student-practice__cat-line"
                                          >
                                             {part.marker ? (
                                                <span className="student-practice__cat-line-mark">
                                                   {part.marker}
                                                </span>
                                             ) : null}
                                             {part.text ? (
                                                <span className="student-practice__cat-line-text">
                                                   {part.text}
                                                </span>
                                             ) : null}
                                          </span>
                                       ))}
                                    </div>
                                    {Number(stat?.total) > 0 ? (
                                       <span className="sTest__srOnly">
                                          {t("progress_aria", { ok: stat.green, bad: stat.red, total: stat.total })}
                                       </span>
                                    ) : null}
                                 </div>

                                 <div
                                    className={
                                       "student-practice__cat-badge" +
                                       (state === "ok"
                                          ? " student-practice__cat-badge--ok"
                                          : "") +
                                       (state === "bad"
                                          ? " student-practice__cat-badge--bad"
                                          : "") +
                                       (state === "none"
                                          ? " student-practice__cat-badge--none"
                                          : "") +
                                       (!stat && catStatsLoading
                                          ? " student-practice__cat-badge--loading"
                                          : "")
                                    }
                                 >
                                    {catScoreText || getCatCount(c)}
                                 </div>
                              </button>
                           );
                        })
                     )}
                  </div>
               )}

               {homeTab === "tickets" && (
                  <div className="student-practice__grid">
                     {tickets.map((tkt) => {
                        const info = ticketCardInfoMap[tkt.id];
                        const cls =
                           "student-practice__ticket" +
                           (info?.state ? ` student-practice__ticket--${info.state}` : "") +
                           (info?.hasInProgress
                              ? " student-practice__ticket--in-progress"
                              : "");
                        // scorul doar după primul răspuns (la biletele neîncepute „0/24” doar încărca grila)
                        const hasStats = Boolean(info && info.total > 0 && info.green + info.red > 0);
                        const name = t("ticket_n", { n: tkt.nr });
                        return (
                           <button
                              key={tkt.id}
                              className={cls}
                              // fundalul e bara: verde cât ai corect, roșu cât ai greșit (ca zilele din calendar)
                              style={progressFill(info)}
                              onClick={() => enterTicket(tkt.id)}
                              disabled={loading}
                              title={t(
                                 info?.hasInProgress
                                    ? "resume_ticket_title"
                                    : "start_ticket_title",
                                 { n: tkt.nr },
                              )}
                              aria-label={
                                 hasStats
                                    ? `${name}, ${t("progress_aria", { ok: info.green, bad: info.red, total: info.total })}`
                                    : name
                              }
                           >
                              <span className="student-practice__ticket-title">{name}</span>
                              {hasStats || info?.hasInProgress ? (
                                 <span className="sTest__pill" aria-hidden="true">
                                    {info?.hasInProgress ? (
                                       <ReactSVG src={playIcon} className="sTest__pillPlay react-icon" />
                                    ) : null}
                                    {hasStats ? `${info.green}/${info.total}` : null}
                                 </span>
                              ) : null}
                           </button>
                        );
                     })}
                  </div>
               )}
            </>
         )}

         {view === "test" && ticket && (
            <>
               <div className="pageToolbar sTest__toolbar sTest__toolbar--test">
                  <ButtonUI size="lg" variant="subtle" icon="arrow" onClick={() => finalizeAttempt("back-button")}>
                     {t("back")}
                  </ButtonUI>
                  {canResetTicket && (
                     <div className="pageToolbar__controls">
                        <ButtonUI
                           size="lg"
                           variant="subtle"
                           confirm={t("reset_yes")}
                           confirmTitle={t("reset_confirm")}
                           cancelLabel={t("reset_no")}
                           confirming={resetConfirmOpen}
                           onConfirmingChange={setResetConfirmOpen}
                           onClick={resetCurrentTicket}
                           loading={resetting}
                           disabled={metrics.answered === 0 || hasPendingSync}
                        >
                           {t("reset_ticket")}
                        </ButtonUI>
                     </div>
                  )}
               </div>

               <div className="student-test__statusboard">
                  {statusBoard.map(({ i, status }) => (
                     <button
                        key={i}
                        className={
                           "student-test__dot" +
                           (i === idx ? " student-test__dot--current" : "") +
                           (status === "ok" ? " student-test__dot--ok" : "") +
                           (status === "bad" ? " student-test__dot--bad" : "") +
                           (status === "wait" ? " student-test__dot--none" : "")
                        }
                        title={t("question_n", { n: i + 1 })}
                        onClick={() => jumpTo(i)}
                     >
                        {i + 1}
                     </button>
                  ))}
               </div>

               {current && (
                  <div className="student-test__question">
                     <div className="student-test__qtext" ref={qTextRef}>
                        {current.text}
                     </div>
                     <div className="student-test__row">
                        <div className="student-test__media">
                           <div className="student-test__qimage-wrapper">
                              {current?.image && (
                                 <img
                                    key={`${keySalt}-${current.id}-${idx}`}
                                    className="student-test__qimage"
                                    src={current.image}
                                    alt={t("question_alt")}
                                    onError={(e) =>
                                       (e.currentTarget.hidden = true)
                                    }
                                 />
                              )}
                           </div>
                           <QuestionStatBar
                              correct={currentStats.correct}
                              wrong={currentStats.wrong}
                              label={t("question_stat_aria", {
                                 ok: currentStats.correct,
                                 bad: currentStats.wrong,
                              })}
                           />
                        </div>

                        <div className="student-test__media student-test__media--mobile">
                           {current?.image && (
                              <div className="student-test__qimage-wrapper student-test__qimage-wrapper--mobile">
                                 <img
                                    key={`${keySalt}-${current.id}-${idx}-m`}
                                    className="student-test__qimage"
                                    src={current.image}
                                    alt={t("question_alt")}
                                    onError={(e) =>
                                       (e.currentTarget.hidden = true)
                                    }
                                 />
                              </div>
                           )}
                           <QuestionStatBar
                              correct={currentStats.correct}
                              wrong={currentStats.wrong}
                              label={t("question_stat_aria", {
                                 ok: currentStats.correct,
                                 bad: currentStats.wrong,
                              })}
                           />
                        </div>

                        <div className="student-test__answers-wrapper">
                           <div className="student-test__answers">
                              {(current.answers || []).map((ans, i) => {
                                 const saved = answersMap[current.id];
                                 const selected = saved?.selected;

                                 const correctIdx = Number.isInteger(
                                    saved?.correctIdx,
                                 )
                                    ? saved.correctIdx
                                    : sessionKind === "ticket" &&
                                        Number.isInteger(correctMap[current.id])
                                      ? correctMap[current.id]
                                      : null;

                                 const isSelected = selected === i;
                                 const hasCorrectIdx =
                                    Number.isInteger(correctIdx);

                                 const isCorrectOption =
                                    selected != null &&
                                    (hasCorrectIdx
                                       ? i === Number(correctIdx)
                                       : saved?.correct === true && isSelected);

                                 const isWrongSelected =
                                    selected != null &&
                                    (hasCorrectIdx
                                       ? isSelected && i !== Number(correctIdx)
                                       : saved?.correct === false &&
                                         isSelected);

                                 const className =
                                    "student-test__answer" +
                                    (isCorrectOption
                                       ? " student-test__answer--correct"
                                       : "") +
                                    (isWrongSelected
                                       ? " student-test__answer--wrong-selected"
                                       : "") +
                                    (isSelected
                                       ? " student-test__answer--selected"
                                       : "");

                                 const isDisabled =
                                    !!saved?.pending ||
                                    saved?.selected != null ||
                                    loading;

                                 return (
                                    <button
                                       key={i}
                                       className={className}
                                       onClick={() =>
                                          sessionKind === "category"
                                             ? handleCategoryAnswerLocal(i)
                                             : onChooseTicket(i)
                                       }
                                       title={t("choose_answer")}
                                       disabled={isDisabled}
                                    >
                                       {ans}
                                    </button>
                                 );
                              })}
                           </div>
                           <div className="student-test__footer">
                              <div className="student-test__actions">
                                 <ButtonUI
                                    size="lg"
                                    variant="subtle"
                                    pressed={isCurrentHintOpen}
                                    onClick={toggleCurrentHint}
                                    aria-expanded={isCurrentHintOpen}
                                 >
                                    {isCurrentHintOpen ? t("hint_hide") : t("hint_show")}
                                 </ButtonUI>
                                 <div className="student-test__spacer" />
                                 <ButtonUI size="lg" variant="subtle" onClick={goNext} disabled={idx >= total - 1}>
                                    {t("next")}
                                 </ButtonUI>

                                 {allAnsweredNow && (
                                    <ButtonUI
                                       size="lg"
                                       variant="primary"
                                       onClick={() => finalizeAttempt("user-finish")}
                                       disabled={hasPendingSync || loading}
                                    >
                                       {t("finish")}
                                    </ButtonUI>
                                 )}
                              </div>
                              {isCurrentHintOpen ? (
                                 <div className="student-test__hint-text">
                                    {currentHintText || t("hint_empty")}
                                 </div>
                              ) : null}
                           </div>
                        </div>
                     </div>
                  </div>
               )}
            </>
         )}
      </div>
   );
}
